import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, Col, Container, Form, FormLabel, Row } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import Select, { MultiValue } from 'react-select';
import DatePicker from 'react-datepicker';
import { fi } from 'date-fns/locale';
import { format } from 'date-fns';
import {
  addStatusToast,
  DataTable,
  DataTableItem,
  DeleteConfirmation,
  InputCheck,
  InputRange,
  PaginationComponent,
  SearchFilterDropdown,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AccommodationListItem,
  deleteAccommodationAsync,
  getAllAccommodationsAsync,
  resetFilters,
  selectAccommodations,
  selectAccommodationsLoadingStatus,
  selectAccommodationsPagination,
  selectAccommodationsSearchFilter,
  setSearchFilters,
} from './accommodationsSearchSlice';
import { LOADING_STATUS } from '../../common/constants';
import AccommodationAutoCompleteSearchBar from '../autocomplete/AccommodationAutoCompleteSearchBar';
import { AutoSuggestOption } from '../autocomplete/autocompleteSlice';
import {
  AccommodationAttribute,
  fetchAttributes,
  selectDistanceAttributes,
  selectEquipmentAttributes,
} from '../searchBookableAccommodations/attributesSlice';
import { getPriceGroupTags, selectPriceGroupTags } from '../tags/tagsSlice';
import {
  AccommodationDetails,
  getAccommodationDetailsBasicAsync,
  saveAccommodationAsync,
  updateAccommodationAsync,
} from './accommodationsSlice';
import { InitialPaginationParameters } from '../../common/types/pagination.types';
import { dateToDateOnlyString } from '../../common/utils/dateUtils';
import { useLocale } from '../../locales/useLocale';

function Accommodations() {
  const dispatch = useAppDispatch();
  const { currentLocale } = useLocale();
  const accommodations = useAppSelector(selectAccommodations);
  const searchFilter = useAppSelector(selectAccommodationsSearchFilter);
  const equipmentAttributes = useAppSelector(selectEquipmentAttributes);
  const distanceAttributes = useAppSelector(selectDistanceAttributes);
  const priceGroupTags = useAppSelector(selectPriceGroupTags);

  const accommodationsLoadingStatus = useAppSelector(
    selectAccommodationsLoadingStatus,
  );
  const accommodationsPagination = useAppSelector(
    selectAccommodationsPagination,
  );
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [deletableAccommodationId, setDeletableAccommodationId] =
    useState<string>('');
  const [deleteConfirmationMessage, setDeleteConfirmationMessage] =
    useState<string>('');
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] =
    useState<boolean>(false);
  const [selectedAutoSuggestOptions, setSelectedAutoSuggestOptions] =
    React.useState<MultiValue<AutoSuggestOption>>([]);

  const [hasSearchBeenMade, setHasSearchBeenMade] = useState<boolean>(false);
  const [freeTextUpdated, setFreeTextUpdated] = useState<boolean>(false);
  const [accommodationTableItems, setAccommodationTableItems] = useState<
    DataTableItem[]
  >([]);

  useEffect(() => {
    if (accommodations && accommodations.length > 0) {
      const formattedAccommodationList: DataTableItem[] = [];
      accommodations?.forEach(item => {
        formattedAccommodationList.push({
          ItemId: item.id,
          Items: [
            item.accommodationNumber.toString(),
            item.name,
            item.municipality,
            item.skiResort,
            `${item.beds} + ${item.extraBeds}`,
          ],
          Url: `/accommodations/details/${item.id}`,
        });
      });
      setAccommodationTableItems(formattedAccommodationList);
    }
  }, [accommodations]);

  const navigate = useNavigate();

  useEffect(() => {
    if (
      hasSearchBeenMade &&
      accommodationsLoadingStatus === LOADING_STATUS.IDLE &&
      accommodationsPagination.totalCount === 1 &&
      accommodations?.length === 1
    ) {
      navigate(`/accommodations/details/${accommodations[0].id}`);
    }
  }, [
    accommodations,
    accommodationsPagination,
    accommodationsLoadingStatus,
    hasSearchBeenMade,
  ]);

  useEffect(() => {
    if (freeTextUpdated) {
      dispatch(getAllAccommodationsAsync(searchFilter));
      setFreeTextUpdated(false);
      setHasSearchBeenMade(true);
    }
  }, [dispatch, searchFilter, hasSearchBeenMade]);

  useEffect(() => {
    dispatch(fetchAttributes(currentLocale));
  }, [dispatch, currentLocale]);

  useEffect(() => {
    dispatch(getPriceGroupTags());
  }, [dispatch]);

  // Set delete confirmation modal id, text and show modal
  const showDeleteModal = (AccommodationId: string) => {
    const accommodationById: AccommodationListItem = accommodations.find(
      a => a.id === AccommodationId,
    );
    setDeletableAccommodationId(AccommodationId);
    setDeleteConfirmationMessage(
      `Haluatko varmasti poistaa majoituksen ${accommodationById.name}?`,
    );
    setIsDeleteConfirmationVisible(true);
  };

  // Passes delete command to backend, hides confirmation modal
  function deleteAccommodation(accommodationId: string) {
    dispatch(deleteAccommodationAsync(accommodationId));
    setIsDeleteConfirmationVisible(false);
  }

  const HandlePaginationClick = (Page: number) => {
    const paginatedFiltering = {
      ...searchFilter,
      PageNumber: Page,
    };

    dispatch(setSearchFilters(paginatedFiltering));
    dispatch(getAllAccommodationsAsync(paginatedFiltering));
  };

  const doSearch = async event => {
    setFreeTextUpdated(true);
    event.preventDefault();

    const searchStringFiltering = {
      ...searchFilter,
      PageNumber: InitialPaginationParameters.PageNumber,
      filterCriteria: {
        ...searchFilter.filterCriteria,
        searchString: searchTerm,
      },
    };

    await dispatch(setSearchFilters(searchStringFiltering));
  };
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const triggerPlainSearch = searchParams.get('triggerPlainSearch');
    if (triggerPlainSearch === 'true') {
      // Perform the plain search logic
      dispatch(getAllAccommodationsAsync(searchFilter));
    }
  }, [location, dispatch]);
  const handleSelect = (selectedItems: MultiValue<AutoSuggestOption>) => {
    const uniqueItems = selectedItems.reduce<AutoSuggestOption[]>(
      (acc, item) => {
        if (!acc.some(existingItem => existingItem.value === item.value)) {
          acc.push(item);
        }
        return acc;
      },
      [],
    );

    const autosuggestFiltering = {
      ...searchFilter,
      filterCriteria: {
        ...searchFilter.filterCriteria,
        autosuggestions: uniqueItems,
      },
    };

    setSelectedAutoSuggestOptions(uniqueItems);
    dispatch(setSearchFilters(autosuggestFiltering));
  };

  const handleSelectFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          [name]: value,
        },
      }),
    );
  };

  const handleSearchFilterChange = (field: string, value: unknown) => {
    const formattedValue =
      value instanceof Date ? format(value, 'yyyy-MM-dd') : value;

    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          [field]: formattedValue,
        },
      }),
    );
  };

  const handleBooleanFilterChange = (field: string, value: unknown) => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          [field]: value,
        },
      }),
    );
  };

  const handleRangeFilterChange = (name: string, value: number) => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          [name]: value,
        },
      }),
    );
  };

  const handleShowDeletedFilterChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { id } = e.target;
    const showDeleted = id === 'poistetut';

    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          showDeleted,
        },
      }),
    );
  };

  const handleOnSaleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;
    const onSale = id === 'onSaleOn';

    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          onSale,
        },
      }),
    );
  };

  const handleSearchTermChange = (inputValue: string) => {
    if (inputValue !== '') {
      const searchStringFiltering = {
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          searchString: inputValue,
        },
      };

      dispatch(setSearchFilters(searchStringFiltering));
    }
    setSearchTerm(inputValue);
  };

  const handleEquipmentInputChanges = (event, typeName, mainAttributeId) => {
    const { target } = event;

    if (typeName === 'Boolean') {
      const propertyName = 'booleanAttributes';
      const selectedAttributes = [...searchFilter.filterCriteria[propertyName]];
      const attributeId = target.name;

      if (target.checked) {
        selectedAttributes.push(attributeId);
      } else {
        const index = selectedAttributes.indexOf(attributeId);
        if (index !== -1) {
          selectedAttributes.splice(index, 1);
        }
      }

      const booleanFiltering = {
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          booleanAttributes: selectedAttributes,
        },
      };

      dispatch(setSearchFilters(booleanFiltering));
    }

    /*
    if (typeName === 'MultiSelect') {
      const propertyName = 'multiSelectAttributes';
      const selectedAttributes = [...searchFilter.filterCriteria[propertyName]];
      const attributeId = target.name;

      if (target.checked) {
        selectedAttributes.push({
          attributeId: mainAttributeId,
          optionId: attributeId,
        });
      } else {
        const index = selectedAttributes.findIndex(
          attr => attr.optionId === attributeId,
        );
        if (index !== -1) {
          selectedAttributes.splice(index, 1);
        }
      }

      const multiSelectFiltering = {
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          multiSelectAttributes: selectedAttributes,
        },
      };

      dispatch(setSearchFilters(multiSelectFiltering));
    }
    */

    if (typeName === 'FreeText') {
      const propertyName = 'textAttributes';
      throw new Error('Not implemented');
    }
  };

  const handleEquipmentSelectChanges = (selectedOptions, attribute) => {
    // Get existing selections for all other attributes
    const otherAttributeSelections =
      searchFilter.filterCriteria.multiSelectAttributes?.filter(
        attr => attr.attributeId !== attribute.attributeId,
      ) || [];

    // Create selections for current attribute
    const currentAttributeSelections = selectedOptions.map(option => ({
      attributeId: attribute.attributeId,
      optionId: option.value,
    }));

    // Combine both selections
    const allSelections = [
      ...otherAttributeSelections,
      ...currentAttributeSelections,
    ];

    const multiSelectFiltering = {
      ...searchFilter,
      filterCriteria: {
        ...searchFilter.filterCriteria,
        multiSelectAttributes: allSelections,
      },
    };

    dispatch(setSearchFilters(multiSelectFiltering));
  };

  const handleDistanceInputChanges = (value, attributeId, typeName) => {
    if (typeName === 'NumericMin') {
      const propertyName = 'minimumNumericAttributes';
      const selectedAttributes = {
        ...searchFilter.filterCriteria[propertyName],
      };

      if (value > 0) {
        selectedAttributes[attributeId] = value;
      } else if (
        Object.prototype.hasOwnProperty.call(selectedAttributes, attributeId)
      ) {
        // Remove the attribute if it exists
        delete selectedAttributes[attributeId];
      }

      // Update the search filter with the modified MinimumNumericAttributes
      const minimumNumericFiltering = {
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          minimumNumericAttributes: selectedAttributes,
        },
      };

      dispatch(setSearchFilters(minimumNumericFiltering));
    }

    if (typeName === 'NumericMax') {
      const propertyName = 'maximumNumericAttributes';
      const selectedAttributes = {
        ...searchFilter.filterCriteria[propertyName],
      };

      if (value > 0) {
        selectedAttributes[attributeId] = value;
      } else if (
        Object.prototype.hasOwnProperty.call(selectedAttributes, attributeId)
      ) {
        // Remove the attribute if it exists
        delete selectedAttributes[attributeId];
      }

      // Update the search filter with the modified MinimumNumericAttributes
      const minimumNumericFiltering = {
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          maximumNumericAttributes: selectedAttributes,
        },
      };

      dispatch(setSearchFilters(minimumNumericFiltering));
    }
  };

  const NO_PRICE_GROUP_VALUE = '__NO_PRICE_GROUP__';

  const handleTagFilterChange = (
    selectedOptions: MultiValue<{ label: string; value: string }>,
  ) => {
    const lastSelected = selectedOptions[selectedOptions.length - 1];
    const isNoPriceGroup =
      lastSelected?.value === NO_PRICE_GROUP_VALUE || (!lastSelected && false);

    if (isNoPriceGroup) {
      dispatch(
        setSearchFilters({
          ...searchFilter,
          filterCriteria: {
            ...searchFilter.filterCriteria,
            tagIds: [],
            noPriceGroup: true,
          },
        }),
      );
    } else {
      const selectedIds = selectedOptions
        .filter(opt => opt.value !== NO_PRICE_GROUP_VALUE)
        .map(opt => opt.value);
      dispatch(
        setSearchFilters({
          ...searchFilter,
          filterCriteria: {
            ...searchFilter.filterCriteria,
            tagIds: selectedIds,
            noPriceGroup: false,
          },
        }),
      );
    }
  };

  const doClear = () => {
    setSearchTerm('');
    setSelectedAutoSuggestOptions([]);
    dispatch(resetFilters());
  };

  const restoreItem = (id: string) => {
    const item = accommodations.find(
      ac => ac.id.toLowerCase() === id.toLowerCase(),
    );
    dispatch(getAccommodationDetailsBasicAsync(item.id)).then(res => {
      if (res.payload) {
        const updateData = res.payload;
        if (!updateData.isAllowedToEdit) {
          dispatch(
            addStatusToast({
              Title: 'Ristiinmyynti kohde',
              Message: `Palauta omassa varaamossa!`,
              IsFailed: true,
            }),
          );
          return;
        }
        const updatedValue = { ...updateData, isDeleted: false };
        dispatch(updateAccommodationAsync(updatedValue)).then(res2 => {
          if (res2.payload && res.meta.requestStatus === 'fulfilled') {
            dispatch(
              addStatusToast({
                Title: 'Kohde palautettu',
                IsFailed: false,
                Message: `Kohde palautettu onnistuneesti!`,
              }),
            );
            dispatch(getAllAccommodationsAsync(searchFilter));
          }
        });
      }
    });
  };

  const equipmentAttributesPerColumn = 12;
  const totalEquipmentAttributeColumns = Math.ceil(
    equipmentAttributes.length / equipmentAttributesPerColumn,
  );
  const distanceAttributesPerColumn = 7;
  const totalDistanceAttributeColumns = Math.ceil(
    distanceAttributes.length / distanceAttributesPerColumn,
  );
  const initialAccommodationState: AccommodationDetails = {
    id: '',
    accommodationNumber: 0,
    beds: 0,
    extraBeds: 0,
    novelty: null,
    translations: [],
    internalQualityRating: '',
    onSale: null,
    showInOwnerPage: null,
    qualityRating: 0,
    notOnSaleDays: 0,
    agentNumber: 0,
    visibleOnFi: null,
    visibleOnEn: null,
    visibleOnRu: null,
    keyBox: null,
    countryId: '',
    regionId: '',
    municipalityId: '',
    internalNotes: [],
    publicNotes: [],
    accommodationGroupData: {
      id: '',
    },
    accommodationAttributes: [],
    accommodationAccountRoleCollection: [],
    fireSafety: false,
    geoCode: {
      latitude: null,
      longitude: null,
    },
    optiWatti: false,

    addOns: [],
    addressId: null,
    placeId: null,
    skiResortId: null,
    leviItem: false,
    interhomeService: false,
    lastMinuteOffer: false,
    sportumId: '',
    accounts: [],
    useGredi: false,
    isCapacityProduct: false,
  };
  const [accommodation, setAccommodation] = useState(initialAccommodationState);
  const addAccommodation = () => {
    dispatch(saveAccommodationAsync(accommodation)).then(res => {
      navigate(`/accommodations/details/${res.payload}`, {
        state: {
          backUrl: `/accommodations/search`,
        },
      });
    });
  };

  const handleRemoveFilter = (filterKey: string) => {
    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          [filterKey]: null,
        },
      }),
    );
  };

  const handleRemoveCreatedDatesFilter = () => {
    const updatedSearchFilter = {
      ...searchFilter,
      filterCriteria: {
        ...searchFilter.filterCriteria,
      },
    };
    delete updatedSearchFilter.filterCriteria.createdTo;
    delete updatedSearchFilter.filterCriteria.createdFrom;

    dispatch(setSearchFilters(updatedSearchFilter));
  };

  const handleRemoveFilterValue = (filterKey: string, value: unknown) => {
    const updatedFilterCriteria = { ...searchFilter.filterCriteria };

    if (Array.isArray(updatedFilterCriteria[filterKey])) {
      updatedFilterCriteria[filterKey] = updatedFilterCriteria[
        filterKey
      ].filter((item: unknown) => item !== value);
    }

    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: updatedFilterCriteria,
      }),
    );
  };

  const handleRemoveEquipmentFilter = (attributeId: string) => {
    const updatedBooleanAttributes =
      searchFilter.filterCriteria.booleanAttributes.filter(
        id => id !== attributeId,
      );

    const updatedMultiSelectAttributes =
      searchFilter.filterCriteria.multiSelectAttributes.filter(
        attr => attr.optionId !== attributeId,
      );

    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          booleanAttributes: updatedBooleanAttributes,
          multiSelectAttributes: updatedMultiSelectAttributes,
        },
      }),
    );
  };

  const handleRemoveDistanceFilter = (attributeId: string) => {
    const updatedMinimumNumericAttributes = {
      ...searchFilter.filterCriteria.minimumNumericAttributes,
    };
    const updatedMaximumNumericAttributes = {
      ...searchFilter.filterCriteria.maximumNumericAttributes,
    };

    delete updatedMinimumNumericAttributes[attributeId];
    delete updatedMaximumNumericAttributes[attributeId];

    dispatch(
      setSearchFilters({
        ...searchFilter,
        filterCriteria: {
          ...searchFilter.filterCriteria,
          minimumNumericAttributes: updatedMinimumNumericAttributes,
          maximumNumericAttributes: updatedMaximumNumericAttributes,
        },
      }),
    );
  };

  const getDistanceAttributeNameById = attributeId => {
    const attribute = distanceAttributes.find(
      attr => attr.attributeId === attributeId,
    );
    return attribute ? attribute.name : attributeId;
  };

  const getEquipmentAttributeNameById = attributeId => {
    const attribute = equipmentAttributes.find(
      attr => attr.attributeId === attributeId,
    );
    return attribute ? attribute.name : attributeId;
  };

  const getMultiSelectAttributeNameById = (attributeId, optionId) => {
    const attribute = equipmentAttributes.find(
      attr => attr.attributeId === attributeId,
    );
    if (attribute) {
      const option = attribute.multiSelectOptions.find(
        opt => opt.optionId === optionId,
      );
      return option ? option.name : optionId;
    }
    return attributeId;
  };

  const formatSelectedValue = (label, value, filterKey: string) => {
    if (value === null || value === undefined || value === 0 || value === '0') {
      return null;
    }

    let displayValue = value;
    if (typeof value === 'boolean') {
      displayValue = value ? 'Kyllä' : 'Ei';
    }

    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {displayValue}
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleRemoveFilter(filterKey)}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  const formatSelectedValues = (label, values, filterKey: string) => {
    if (!values || values.length === 0) {
      return null;
    }

    return (
      <>
        <span className="small fw-medium ms-8">{label}:</span>
        {values.map(value => (
          <span
            key={value}
            className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8"
          >
            {value}
            <Button
              variant="link"
              className="p-0"
              onClick={() => handleRemoveFilterValue(filterKey, value)}
            >
              <span className="bi bi-x" />
            </Button>
          </span>
        ))}
      </>
    );
  };

  const formatSelectedDates = (
    label: string,
    value: Date,
    value2: Date,
    filterKey: string,
    filterKey2: string,
  ) => {
    if (
      value === null ||
      value2 === null ||
      value === undefined ||
      value2 === undefined ||
      value < new Date('2020-01-01') ||
      value2 < new Date('2020-01-01')
    ) {
      return null;
    }
    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {dateToDateOnlyString(value)} - {dateToDateOnlyString(value2)}
        <Button
          variant="link"
          className="p-0"
          onClick={() => {
            handleRemoveCreatedDatesFilter();
          }}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  const formatSelectedEquipments = (
    label,
    equipments,
    multiSelectEquipments,
  ) => {
    if (
      (!equipments || equipments.length === 0) &&
      (!multiSelectEquipments || multiSelectEquipments.length === 0)
    ) {
      return null;
    }

    const booleanAttributes = equipments.map(id => ({
      id,
      name: getEquipmentAttributeNameById(id).toLowerCase(),
    }));

    const multiSelectAttributes = multiSelectEquipments.map(attr => ({
      id: attr.optionId,
      name: getMultiSelectAttributeNameById(
        attr.attributeId,
        attr.optionId,
      ).toLowerCase(),
    }));

    const allAttributes = [...booleanAttributes, ...multiSelectAttributes];

    return (
      <>
        <span className="small fw-medium ms-8">{label}:</span>
        {allAttributes.map(attribute => (
          <span
            key={attribute.id}
            className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8"
          >
            {attribute.name}
            <Button
              variant="link"
              className="p-0"
              onClick={() => handleRemoveEquipmentFilter(attribute.id)}
            >
              <span className="bi bi-x" />
            </Button>
          </span>
        ))}
      </>
    );
  };

  const formatSelectedDistances = (label, distances) => {
    if (!distances || Object.keys(distances).length === 0) {
      return null;
    }
    return (
      <>
        <span className="small fw-medium ms-8">{label}:</span>
        {Object.entries(distances).map(([key, value]) => (
          <span
            key={key}
            className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8"
          >
            {getDistanceAttributeNameById(key).toLowerCase()}: {value} km
            <Button
              variant="link"
              className="p-0"
              onClick={() => handleRemoveDistanceFilter(key)}
            >
              <span className="bi bi-x" />
            </Button>
          </span>
        ))}
      </>
    );
  };

  type QualityRatingOption = {
    label: string;
    value: number;
  };

  const qualityRatingOptions: QualityRatingOption[] = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
  ];

  // draws elements to browser
  return (
    <>
      <Helmet>
        <title>Majoitukset</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Majoitukset ja Ryhmät']}
          Children={
            <Button className="btn btn-blue ps-16" onClick={addAccommodation}>
              <Icon CssClass="me-8" AriaLabel="Uusi" Name="plus" />
              Lisää uusi
            </Button>
          }
          Title="Majoitukset"
        />

        <AccommodationAutoCompleteSearchBar
          onSelect={handleSelect}
          selectedOptions={selectedAutoSuggestOptions}
          isLoading={accommodationsLoadingStatus === LOADING_STATUS.LOADING}
          searchHandler={doSearch}
          handleSearchTermChange={handleSearchTermChange}
          searchTerm={searchTerm}
        >
          <>
            {accommodationsLoadingStatus === LOADING_STATUS.IDLE && (
              <div className="position-relative px-24 pt-0 pb-8">
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={false}
                  Id="users-filter-created"
                  Label="Luotu"
                  Value={
                    !!searchFilter.filterCriteria.createdFrom ||
                    !!searchFilter.filterCriteria.createdTo
                  }
                >
                  <Form.Group className="mb-3">
                    <Form.Label>Alkaen</Form.Label>
                    <DatePicker
                      locale={fi}
                      selected={
                        searchFilter.filterCriteria.createdFrom
                          ? new Date(searchFilter.filterCriteria.createdFrom)
                          : null
                      }
                      dateFormat="dd.MM.yyyy"
                      onChange={date =>
                        handleSearchFilterChange('createdFrom', date)
                      }
                      showWeekNumbers
                      className="form-control"
                      showYearDropdown
                      scrollableYearDropdown
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Päättyen</Form.Label>
                    <DatePicker
                      locale={fi}
                      selected={
                        searchFilter.filterCriteria.createdTo
                          ? new Date(searchFilter.filterCriteria.createdTo)
                          : null
                      }
                      minDate={
                        searchFilter.filterCriteria.createdFrom
                          ? new Date(searchFilter.filterCriteria.createdFrom)
                          : null
                      }
                      dateFormat="dd.MM.yyyy"
                      onChange={date =>
                        handleSearchFilterChange('createdTo', date)
                      }
                      showWeekNumbers
                      className="form-control"
                      disabled={!searchFilter.filterCriteria.createdFrom}
                      showYearDropdown
                      scrollableYearDropdown
                    />
                  </Form.Group>
                </SearchFilterDropdown>
                <SearchFilterDropdown
                  DefaultValue={0}
                  Id="accommodations-filter-persons"
                  Label="Henkilömäärä"
                  MinWidth={300}
                  Value={searchFilter.filterCriteria.maxPersonCount ?? 0}
                  ShowCloseButton={false}
                >
                  <Form.Select
                    aria-label="Henkilömäärä"
                    name="maxPersonCount"
                    onChange={handleSelectFilterChange}
                    value={searchFilter.filterCriteria.maxPersonCount ?? 0}
                  >
                    <option value="0">Valitse henkilömäärä</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                    <option value="8">8</option>
                    <option value="9">9</option>
                    <option value="10">10</option>
                    <option value="11">11</option>
                    <option value="12">12</option>
                    <option value="13">13</option>
                    <option value="14">14</option>
                    <option value="15">15</option>
                    <option value="16">16</option>
                    <option value="17">17</option>
                    <option value="18">18</option>
                    <option value="19">19</option>
                    <option value="20">20</option>
                  </Form.Select>
                </SearchFilterDropdown>
                <SearchFilterDropdown
                  DefaultValue={0}
                  Id="accommodations-filter-area"
                  Label="Pinta-ala"
                  MinWidth={360}
                  Value={searchFilter.filterCriteria.area ?? 0}
                  ShowCloseButton={false}
                >
                  <InputRange
                    ClassName="mb-0"
                    HandleChange={(value: number) =>
                      handleRangeFilterChange('area', value)
                    }
                    Id="accommodations-filter-area-range"
                    Name="area"
                    Max={1000}
                    Min={0}
                    Step={10}
                    Unit="m2"
                    Value={searchFilter.filterCriteria.area ?? 0}
                  />
                </SearchFilterDropdown>
                <SearchFilterDropdown
                  DefaultValue={0}
                  Id="accommodations-filter-bedrooms"
                  Label="Makuuhuoneet ja WC:t"
                  MinWidth={360}
                  Value={
                    searchFilter.filterCriteria.numberOfBedrooms ??
                    searchFilter.filterCriteria.numberOfToilets ??
                    0
                  }
                  ShowCloseButton={false}
                >
                  <Form.Group className="mb-16">
                    <Form.Label>Makuuhuoneiden määrä</Form.Label>
                    <Form.Select
                      aria-label="Makuuhuoneiden määrä"
                      name="numberOfBedrooms"
                      onChange={handleSelectFilterChange}
                      value={searchFilter.filterCriteria.numberOfBedrooms ?? 0}
                    >
                      <option value="0">Valitse määrä</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>WC määrä</Form.Label>
                    <Form.Select
                      aria-label="WC määrä"
                      name="numberOfToilets"
                      onChange={handleSelectFilterChange}
                      value={searchFilter.filterCriteria.numberOfToilets ?? 0}
                    >
                      <option value="0">Valitse määrä</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10</option>
                    </Form.Select>
                  </Form.Group>
                </SearchFilterDropdown>
                <SearchFilterDropdown
                  DefaultValue={0}
                  Id="accommodations-filter-rating"
                  Label="Tähtiluokka"
                  MinWidth={300}
                  Value={
                    searchFilter.filterCriteria.qualityRatings?.length ?? 0
                  }
                  ShowCloseButton={false}
                >
                  <Select
                    isMulti
                    placeholder="Valitse tähtiluokka"
                    options={qualityRatingOptions}
                    value={
                      searchFilter.filterCriteria.qualityRatings?.map(
                        (rating: number) => ({
                          label: rating.toString(),
                          value: rating,
                        }),
                      ) as MultiValue<QualityRatingOption>
                    }
                    onChange={selectedOptions => {
                      const selectedValues = (
                        selectedOptions as MultiValue<QualityRatingOption>
                      ).map(option => option.value);
                      handleSearchFilterChange(
                        'qualityRatings',
                        selectedValues,
                      );
                    }}
                  />
                </SearchFilterDropdown>
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={undefined}
                  Id="accommodations-filter-interhomeService"
                  Label="Interhome kohde"
                  Value={
                    searchFilter.filterCriteria.interhomeService ?? undefined
                  }
                >
                  <Form.Group>
                    <Form.Check
                      type="switch"
                      id="custom-switch"
                      label="Interhome kohde"
                      checked={
                        searchFilter.filterCriteria.interhomeService ?? false
                      }
                      onChange={e =>
                        handleBooleanFilterChange(
                          'interhomeService',
                          e.target.checked,
                        )
                      }
                    />
                  </Form.Group>
                </SearchFilterDropdown>
                <SearchFilterDropdown
                  DefaultValue={0}
                  Id="accommodations-filter-price-group"
                  Label="Hintaryhmä"
                  MinWidth={300}
                  Value={
                    (searchFilter.filterCriteria.tagIds?.length ?? 0) +
                    (searchFilter.filterCriteria.noPriceGroup ? 1 : 0)
                  }
                  ShowCloseButton={false}
                >
                  <Select
                    isMulti
                    placeholder="Valitse hintaryhmä"
                    options={[
                      {
                        label: 'Ei hintaryhmää',
                        value: NO_PRICE_GROUP_VALUE,
                      },
                      ...priceGroupTags.map(tag => ({
                        label: tag.name,
                        value: tag.id,
                      })),
                    ]}
                    value={
                      searchFilter.filterCriteria.noPriceGroup
                        ? [
                            {
                              label: 'Ei hintaryhmää',
                              value: NO_PRICE_GROUP_VALUE,
                            },
                          ]
                        : priceGroupTags
                            .filter(tag =>
                              searchFilter.filterCriteria.tagIds?.includes(
                                tag.id,
                              ),
                            )
                            .map(tag => ({ label: tag.name, value: tag.id }))
                    }
                    onChange={handleTagFilterChange}
                  />
                </SearchFilterDropdown>
                <SearchFilterDropdown
                  DefaultValue={false}
                  FullWidth
                  Id="accommodations-equipment"
                  Label="Varustelu"
                  MinWidth={600}
                  Value={
                    searchFilter.filterCriteria.booleanAttributes.length > 0 ||
                    searchFilter.filterCriteria.multiSelectAttributes.length > 0
                  }
                  ShowCloseButton={false}
                >
                  <Row xs={1} md={2}>
                    {[...Array(totalEquipmentAttributeColumns)].map(
                      (_, columnIndex) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Col key={columnIndex}>
                          {equipmentAttributes
                            .slice(
                              columnIndex * equipmentAttributesPerColumn,
                              (columnIndex + 1) * equipmentAttributesPerColumn,
                            )
                            .map(attribute => (
                              <React.Fragment key={attribute.attributeId}>
                                {attribute.typeName === 'MultiSelect' ? (
                                  <>
                                    <FormLabel>{attribute.name}</FormLabel>
                                    <div
                                      style={{
                                        marginLeft: '20px',
                                        marginBottom: '20px',
                                      }}
                                    >
                                      {/*
                                      {attribute.multiSelectOptions?.map(
                                        option => (
                                          <InputCheck
                                            key={option.optionId}
                                            HandleChange={(
                                              e: AccommodationAttribute,
                                            ) =>
                                              handleEquipmentInputChanges(
                                                e,
                                                attribute.typeName,
                                                attribute.attributeId,
                                              )
                                            }
                                            Id={`accommodations-equipment-${option.optionId}`}
                                            Name={option.optionId}
                                            Label={option.name}
                                            IsRequired={false}
                                            Checked={searchFilter.filterCriteria.multiSelectAttributes.some(
                                              attr =>
                                                attr.optionId ===
                                                option.optionId,
                                            )}
                                          />
                                        ),
                                      )}
                                      */}
                                      <Select
                                        isMulti
                                        placeholder="Valitse"
                                        options={attribute.multiSelectOptions.map(
                                          option => ({
                                            label: option.name,
                                            value: option.optionId,
                                          }),
                                        )}
                                        value={searchFilter.filterCriteria.multiSelectAttributes
                                          .filter(
                                            attr =>
                                              attr.attributeId ===
                                              attribute.attributeId,
                                          )
                                          .map(attr => ({
                                            label:
                                              attribute.multiSelectOptions.find(
                                                option =>
                                                  option.optionId ===
                                                  attr.optionId,
                                              )?.name,
                                            value: attr.optionId,
                                          }))}
                                        onChange={selectedOptions =>
                                          handleEquipmentSelectChanges(
                                            selectedOptions,
                                            attribute,
                                          )
                                        }
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <InputCheck
                                    key={attribute.attributeId}
                                    HandleChange={(e: AccommodationAttribute) =>
                                      handleEquipmentInputChanges(
                                        e,
                                        attribute.typeName,
                                        attribute.attributeId,
                                      )
                                    }
                                    Id={`accommodations-equipment-${attribute.attributeId}`}
                                    Name={attribute.attributeId}
                                    Label={attribute.name}
                                    IsRequired={false}
                                    Checked={searchFilter.filterCriteria.booleanAttributes.includes(
                                      attribute.attributeId,
                                    )}
                                  />
                                )}
                              </React.Fragment>
                            ))}
                        </Col>
                      ),
                    )}
                  </Row>
                </SearchFilterDropdown>
                <SearchFilterDropdown
                  DefaultValue={false}
                  FullWidth
                  Id="accommodations-distances"
                  Label="Etäisyydet"
                  MinWidth={600}
                  Value={
                    Object.keys(
                      searchFilter.filterCriteria.minimumNumericAttributes,
                    ).length > 0 ||
                    Object.keys(
                      searchFilter.filterCriteria.maximumNumericAttributes,
                    ).length > 0
                  }
                  ShowCloseButton={false}
                >
                  <Row xs={1} md={2}>
                    {[...Array(totalDistanceAttributeColumns)].map(
                      (_, columnIndex) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Col key={columnIndex}>
                          {distanceAttributes
                            .slice(
                              columnIndex * distanceAttributesPerColumn,
                              (columnIndex + 1) * distanceAttributesPerColumn,
                            )
                            .map(attribute => (
                              <React.Fragment key={attribute.attributeId}>
                                <Form.Label>
                                  {attribute.name}
                                  {attribute.typeName === 'NumericMin' &&
                                    ' vähintään'}
                                </Form.Label>
                                <InputRange
                                  HandleChange={(e: number) =>
                                    handleDistanceInputChanges(
                                      e,
                                      attribute.attributeId,
                                      attribute.typeName,
                                    )
                                  }
                                  Id={`accommodations-distances-${attribute.attributeId}`}
                                  Name={attribute.attributeId}
                                  Max={30}
                                  Min={0}
                                  Step={0.1}
                                  Unit="km"
                                  Value={
                                    attribute.typeName === 'NumericMin'
                                      ? searchFilter.filterCriteria
                                          .minimumNumericAttributes[
                                          attribute.attributeId
                                        ] || 0
                                      : searchFilter.filterCriteria
                                          .maximumNumericAttributes[
                                          attribute.attributeId
                                        ] || 0
                                  }
                                />
                              </React.Fragment>
                            ))}
                        </Col>
                      ),
                    )}
                  </Row>
                </SearchFilterDropdown>
              </div>
            )}
            <hr
              className={
                accommodationsLoadingStatus === LOADING_STATUS.LOADING
                  ? 'd-none'
                  : 'my-0'
              }
            />
            {accommodationsLoadingStatus === LOADING_STATUS.IDLE && (
              <div className="d-flex align-items-center p-24">
                {searchFilter !== null &&
                  searchFilter.filterCriteria !== null && (
                    <div className="flex-grow-1 me-24">
                      <Form.Check
                        inline
                        label="Aktiiviset"
                        name="showDeleted"
                        type="radio"
                        id="aktiiviset"
                        checked={!searchFilter.filterCriteria.showDeleted}
                        onChange={handleShowDeletedFilterChange}
                      />
                      <Form.Check
                        inline
                        label="Poistetut"
                        name="showDeleted"
                        type="radio"
                        id="poistetut"
                        checked={searchFilter.filterCriteria.showDeleted}
                        onChange={handleShowDeletedFilterChange}
                      />
                      <Form.Check
                        inline
                        label="Myynnissä"
                        name="onSale"
                        type="radio"
                        id="onSaleOn"
                        checked={searchFilter.filterCriteria.onSale}
                        onChange={handleOnSaleFilterChange}
                      />
                      <Form.Check
                        inline
                        label="Ei myynnissä"
                        name="onSale"
                        type="radio"
                        id="onSaleOff"
                        checked={!searchFilter.filterCriteria.onSale}
                        onChange={handleOnSaleFilterChange}
                      />
                    </div>
                  )}
              </div>
            )}
            {accommodationsLoadingStatus === LOADING_STATUS.IDLE && (
              <div className="d-flex align-items-center p-24">
                <div className="d-flex flex-wrap align-items-center gap-8">
                  {formatSelectedDates(
                    'Luotu',
                    searchFilter.filterCriteria.createdFrom,
                    searchFilter.filterCriteria.createdTo,
                    'createdFrom',
                    'createdTo',
                  )}
                  {formatSelectedValue(
                    'Henkilömäärä',
                    searchFilter.filterCriteria.maxPersonCount,
                    'maxPersonCount',
                  )}
                  {formatSelectedValue(
                    'Pinta-ala',
                    searchFilter.filterCriteria.area,
                    'area',
                  )}
                  {formatSelectedValue(
                    'Makuuhuoneet',
                    searchFilter.filterCriteria.numberOfBedrooms,
                    'numberOfBedrooms',
                  )}
                  {formatSelectedValue(
                    'WC määrä',
                    searchFilter.filterCriteria.numberOfToilets,
                    'numberOfToilets',
                  )}
                  {formatSelectedValues(
                    'Tähtiluokka',
                    searchFilter.filterCriteria.qualityRatings,
                    'qualityRatings',
                  )}
                  {formatSelectedValue(
                    'Interhome kohde',
                    searchFilter.filterCriteria.interhomeService,
                    'interhomeService',
                  )}

                  {(searchFilter.filterCriteria.tagIds?.length > 0 ||
                    searchFilter.filterCriteria.noPriceGroup) && (
                    <>
                      <span className="small fw-medium ms-8">Hintaryhmä:</span>
                      {searchFilter.filterCriteria.noPriceGroup && (
                        <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
                          Ei hintaryhmää
                          <Button
                            variant="link"
                            className="p-0"
                            onClick={() => handleTagFilterChange([])}
                          >
                            <span className="bi bi-x" />
                          </Button>
                        </span>
                      )}
                      {searchFilter.filterCriteria.tagIds?.map(tagId => {
                        const tag = priceGroupTags.find(t => t.id === tagId);
                        return (
                          <span
                            key={tagId}
                            className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8"
                          >
                            {tag?.name ?? tagId}
                            <Button
                              variant="link"
                              className="p-0"
                              onClick={() =>
                                handleTagFilterChange(
                                  (searchFilter.filterCriteria.tagIds ?? [])
                                    .filter(id => id !== tagId)
                                    .map(id => {
                                      const t = priceGroupTags.find(
                                        pt => pt.id === id,
                                      );
                                      return {
                                        label: t?.name ?? id,
                                        value: id,
                                      };
                                    }),
                                )
                              }
                            >
                              <span className="bi bi-x" />
                            </Button>
                          </span>
                        );
                      })}
                    </>
                  )}

                  {formatSelectedEquipments(
                    'Varustelu',
                    searchFilter.filterCriteria.booleanAttributes,
                    searchFilter.filterCriteria.multiSelectAttributes,
                  )}

                  {formatSelectedDistances(
                    'Etäisyydet (vähintään)',
                    searchFilter.filterCriteria.minimumNumericAttributes,
                  )}

                  {formatSelectedDistances(
                    'Etäisyydet (enintään)',
                    searchFilter.filterCriteria.maximumNumericAttributes,
                  )}
                </div>
                <Button
                  onClick={doClear}
                  variant="outline-blue"
                  size="sm"
                  className="ms-auto"
                >
                  Tyhjenn&auml; hakuehdot
                </Button>
              </div>
            )}
          </>
        </AccommodationAutoCompleteSearchBar>

        {accommodationsLoadingStatus === LOADING_STATUS.IDLE &&
          accommodations != null &&
          accommodations.length > 0 && (
            <>
              <DataTable
                ColumnWidths={[200, null]}
                DeleteItem={showDeleteModal}
                ItemActions={id => {
                  const item = accommodations.find(
                    ac => ac.id.toLowerCase() === id.toLowerCase(),
                  );
                  if (
                    item !== null &&
                    item !== undefined &&
                    item.isDeleted !== null &&
                    item.isDeleted !== undefined &&
                    item.isDeleted === true
                  ) {
                    return [
                      {
                        ButtonAction: () => {
                          restoreItem(id);
                        },
                        ButtonText: 'Palauta kohde',
                      },
                    ];
                  }
                  return [];
                }}
                Header={[
                  'Mökin numero',
                  'Mökin nimi',
                  'Kunta',
                  'Hiihtokeskus',
                  'Vuoteet',
                ]}
                Items={accommodationTableItems}
                LegendText="majoitusta"
                TotalItems={accommodationsPagination.totalCount}
              />
              <PaginationComponent
                OnPageClick={HandlePaginationClick}
                PaginationData={accommodationsPagination}
              />
            </>
          )}

        {accommodationsLoadingStatus === LOADING_STATUS.IDLE &&
          accommodations != null &&
          accommodations.length < 1 &&
          hasSearchBeenMade && <p>Ei majoituksia.</p>}
      </Container>

      <DeleteConfirmation
        ConfirmDelete={() => deleteAccommodation(deletableAccommodationId)}
        HideModal={() => setIsDeleteConfirmationVisible(false)}
        Id={deletableAccommodationId}
        Message={deleteConfirmationMessage}
        ShowModal={isDeleteConfirmationVisible}
      />
    </>
  );
}

export default Accommodations;
